/**
 * Own frame (friendly side of the top `FrameCluster`, #421). Own hp bar. Same
 * data-* contract as TargetFrame; WP8 e2e uses these.
 *
 * Layout (#421): this component no longer self-positions (`position:
 * absolute` + a hardcoded `left: %`) — it renders as an ordinary flex child
 * of `FrameCluster`'s friendly (left) group, alongside `FriendlyTargetFrame`,
 * extending the same friendly-left/enemy-right convention `UnitFrameStack`
 * (#138) already used for the bottom roster.
 *
 * Champion color (#140): the frame border + "you" label are tinted with the
 * viewer's CHAMPION color (identity accent), while the HP bar stays
 * health-fraction colored — so both signals coexist (identity accent + health).
 * The own champion is the LOCAL PLAYER'S actual picked champion (#244
 * `match.champion` action, `state.ownChampionId`), falling back to
 * `DEFAULT_HUD_CHAMPION_ID` only pre-pick/pre-match when it's still null —
 * the SAME resolution convention `ActionBar.tsx` uses (#271/#275).
 */

import type { ReactElement } from "react";
import { useStore } from "../store/context.js";
import { ownEntity } from "./hud-util.js";
import { championColorCss } from "../champions/championColors.js";
import { DEFAULT_HUD_CHAMPION_ID } from "../champions/abilityLabels.js";
import { FrameCastBar } from "./FrameCastBar.js";
import { FrameResourceBar } from "./FrameResourceBar.js";
import { HpBar } from "./HpBar.js";

export function OwnFrame(): ReactElement {
  const { state } = useStore();
  const me = ownEntity(state.hudSnapshot, state.ownSeat);
  const empty = me === null;
  // The LOCAL PLAYER'S actual picked champion, not a hardcoded placeholder —
  // falls back to DEFAULT_HUD_CHAMPION_ID only pre-pick/pre-match (matches
  // ActionBar's convention, #271/#275).
  const championId = state.ownChampionId ?? DEFAULT_HUD_CHAMPION_ID;
  const accent = championColorCss(championId);
  return (
    <div
      data-testid="own-frame"
      data-empty={empty ? "true" : "false"}
      data-hp={me === null ? "0" : String(me.hp)}
      data-max-hp={me === null ? "0" : String(me.maxHp)}
      data-champion-color={accent}
      style={{
        width: 200,
        padding: 8,
        background: "#161c24",
        border: `2px solid ${accent}`,
        borderRadius: 4,
        pointerEvents: "auto",
      }}
    >
      <div style={{ fontSize: 12, marginBottom: 4, color: accent }}>you</div>
      {me === null ? (
        <div style={{ fontSize: 12, opacity: 0.5 }}>—</div>
      ) : (
        <>
          <HpBar hp={me.hp} maxHp={me.maxHp} testId="own-hp-bar" />
          <FrameResourceBar
            resource={me.resource}
            points={me.points}
            championId={championId}
            testIdPrefix="own"
          />
          <FrameCastBar
            cast={me.cast}
            tick={state.hudSnapshot?.tick ?? 0}
            championId={championId}
            testIdPrefix="own-frame"
          />
        </>
      )}
    </div>
  );
}
